import React, { useMemo, useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import '@uiw/react-md-editor/markdown-editor.css';

/**
 * ReportEditor Component
 * 
 * Advanced report editing interface with tabs for different report sections
 */
const ReportEditor = ({
  sessionId,
  sessionTitle,
  initialData = {},
  onSave,
  onCancel,
  isSaving = false,
  saveErrors: externalSaveErrors = {},
}) => {
  const [activeTab, setActiveTab] = useState('at_glance');
  
  // Parse initial markdown for "Session at a glance" into three fields
  const parseAtGlanceMarkdown = (markdown) => {
    if (!markdown) return { summary: '', keypoints: '', speakers: '' };
    
    const lines = markdown.split('\n');
    let currentSection = null;
    let summary = [];
    let keypoints = [];
    let speakers = [];
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('## Summary')) {
        currentSection = 'summary';
      } else if (trimmed.startsWith('## Keypoints') || trimmed.startsWith('## Key Points')) {
        currentSection = 'keypoints';
      } else if (trimmed.startsWith('## Speakers')) {
        currentSection = 'speakers';
      } else if (currentSection === 'summary') {
        summary.push(line);
      } else if (currentSection === 'keypoints') {
        keypoints.push(line);
      } else if (currentSection === 'speakers') {
        speakers.push(line);
      } else if (!currentSection && trimmed) {
        // Content before any section - assume it's summary
        summary.push(line);
      }
    }
    
    return {
      summary: summary.join('\n').trim(),
      keypoints: keypoints.join('\n').trim(),
      speakers: speakers.join('\n').trim(),
    };
  };
  
  // Parse speakers markdown into structured data
  const parseSpeakersMarkdown = (markdown) => {
    if (!markdown) return [];
    
    const speakers = [];
    const lines = markdown.split('\n');
    let currentSpeaker = null;
    let currentArgument = null;
    let currentArgumentData = {};
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Speaker header (## SpeakerName)
      if (trimmed.startsWith('## ') && !trimmed.startsWith('### ')) {
        // Save previous argument and speaker
        if (currentSpeaker && currentArgument) {
          const speaker = speakers.find(s => s.name === currentSpeaker);
          if (speaker) {
            speaker.arguments.push({ ...currentArgumentData, name: currentArgument });
          }
        }
        
        const speakerName = trimmed.substring(3).trim();
        if (speakerName && !speakers.find(s => s.name === speakerName)) {
          speakers.push({ name: speakerName, arguments: [] });
        }
        currentSpeaker = speakerName;
        currentArgument = null;
        currentArgumentData = {};
      }
      // Argument header (### ArgumentName)
      else if (trimmed.startsWith('### ')) {
        // Save previous argument
        if (currentSpeaker && currentArgument) {
          const speaker = speakers.find(s => s.name === currentSpeaker);
          if (speaker) {
            speaker.arguments.push({ ...currentArgumentData, name: currentArgument });
          }
        }
        
        currentArgument = trimmed.substring(4).trim();
        currentArgumentData = {};
      }
      // Field labels (**Field:** value)
      else if (trimmed.includes(':**') && trimmed.startsWith('**')) {
        const parts = trimmed.split(':**', 2);
        if (parts.length === 2) {
          const fieldName = parts[0].replace(/\*\*/g, '').trim();
          const fieldValue = parts[1].trim();
          
          if (fieldName.toLowerCase() === 'explanation' || fieldName.toLowerCase().includes('explanation')) {
            currentArgumentData.explanation = fieldValue;
          } else if (fieldName.toLowerCase().includes('evidence')) {
            currentArgumentData.evidence = fieldValue;
          } else if (fieldName.toLowerCase().includes('major discussion point') || fieldName.toLowerCase().includes('discussion point')) {
            currentArgumentData.majorDiscussionPoint = fieldValue;
          } else if (fieldName.toLowerCase().includes('agreed with')) {
            currentArgumentData.agreedWith = fieldValue;
          } else if (fieldName.toLowerCase().includes('agreed on')) {
            currentArgumentData.agreedOn = fieldValue;
          } else if (fieldName.toLowerCase().includes('disagreed with')) {
            currentArgumentData.disagreedWith = fieldValue;
          } else if (fieldName.toLowerCase().includes('disagreed on')) {
            currentArgumentData.disagreedOn = fieldValue;
          }
        }
      }
      // Regular content - add to explanation if no field specified
      else if (trimmed && currentArgument && !currentArgumentData.explanation) {
        currentArgumentData.explanation = trimmed;
      }
    }
    
    // Save last argument
    if (currentSpeaker && currentArgument) {
      const speaker = speakers.find(s => s.name === currentSpeaker);
      if (speaker) {
        speaker.arguments.push({ ...currentArgumentData, name: currentArgument });
      }
    }
    
    return speakers.length > 0 ? speakers : [];
  };
  
  const initialAtGlance = parseAtGlanceMarkdown(initialData.session_at_glance_md || '');
  const initialSpeakers = parseSpeakersMarkdown(initialData.speakers_md || '');
  
  const [data, setData] = useState({
    // Session at a glance - structured fields
    at_glance_summary: initialAtGlance.summary,
    at_glance_keypoints: initialAtGlance.keypoints,
    at_glance_speakers: initialAtGlance.speakers,
    // Speakers - structured data
    speakers_data: initialSpeakers,
    // Other fields remain as markdown
    full_report_md: initialData.full_report_md || '',
    in_depth_md: initialData.in_depth_md || '',
  });
  const [savedStates, setSavedStates] = useState({
    at_glance_summary: initialAtGlance.summary,
    at_glance_keypoints: initialAtGlance.keypoints,
    at_glance_speakers: initialAtGlance.speakers,
    speakers_data: initialSpeakers,
    full_report_md: initialData.full_report_md || '',
    in_depth_md: initialData.in_depth_md || '',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({
    at_glance: false,
    full_report: false,
    speakers: false,
    analysis: false,
  });
  const [previewMode, setPreviewMode] = useState('edit'); // 'edit' | 'live' | 'preview'
  const [validationErrors, setValidationErrors] = useState({});
  const [collapsedSpeakers, setCollapsedSpeakers] = useState({});
  const [collapsedArguments, setCollapsedArguments] = useState({});

  const mergedErrors = useMemo(
    () => ({ ...(externalSaveErrors || {}), ...(validationErrors || {}) }),
    [externalSaveErrors, validationErrors]
  );

  // Update data when initialData changes (e.g., after loading from API)
  useEffect(() => {
    const parsedAtGlance = parseAtGlanceMarkdown(initialData.session_at_glance_md || '');
    const parsedSpeakers = parseSpeakersMarkdown(initialData.speakers_md || '');
    const newData = {
      at_glance_summary: parsedAtGlance.summary,
      at_glance_keypoints: parsedAtGlance.keypoints,
      at_glance_speakers: parsedAtGlance.speakers,
      speakers_data: parsedSpeakers,
      full_report_md: initialData.full_report_md || '',
      in_depth_md: initialData.in_depth_md || '',
    };
    
    setData(newData);
    setSavedStates(newData);
    setValidationErrors({});
    setHasUnsavedChanges({
      at_glance: false,
      full_report: false,
      speakers: false,
      analysis: false,
    });
  }, [initialData]);
  
  // Combine structured speakers data back to markdown
  const combineSpeakersToMarkdown = (speakersData) => {
    if (!speakersData || speakersData.length === 0) return '';
    
    const parts = [];
    for (const speaker of speakersData) {
      if (!speaker.name) continue;
      parts.push(`## ${speaker.name}\n\n`);
      
      if (speaker.arguments && speaker.arguments.length > 0) {
        for (const arg of speaker.arguments) {
          if (!arg.name) continue;
          parts.push(`### ${arg.name}\n\n`);
          
          if (arg.explanation) {
            parts.push(`**Explanation:** ${arg.explanation}\n\n`);
          }
          if (arg.evidence) {
            parts.push(`**Evidence:** ${arg.evidence}\n\n`);
          }
          if (arg.majorDiscussionPoint) {
            parts.push(`**Major discussion point:** ${arg.majorDiscussionPoint}\n\n`);
          }
          if (arg.agreedWith) {
            parts.push(`**Agreed with:** ${arg.agreedWith}\n\n`);
          }
          if (arg.agreedOn) {
            parts.push(`**Agreed on:** ${arg.agreedOn}\n\n`);
          }
          if (arg.disagreedWith) {
            parts.push(`**Disagreed with:** ${arg.disagreedWith}\n\n`);
          }
          if (arg.disagreedOn) {
            parts.push(`**Disagreed on:** ${arg.disagreedOn}\n\n`);
          }
          
          parts.push('\n');
        }
      }
      parts.push('\n');
    }
    
    return parts.join('');
  };
  
  const tabs = [
    { id: 'at_glance', label: 'Session at a glance', isStructured: true },
    { id: 'full_report', label: 'Full report', key: 'full_report_md', rows: 20 },
    { id: 'speakers', label: 'Speakers', isStructured: true },
    { id: 'analysis', label: 'In-depth analysis', key: 'in_depth_md', rows: 20 },
  ];
  
  // Helper to combine at_glance fields into markdown format
  const combineAtGlanceToMarkdown = (summary, keypoints, speakers) => {
    const parts = [];
    if (summary) parts.push(`## Summary\n\n${summary}`);
    if (keypoints) parts.push(`## Keypoints\n\n${keypoints}`);
    if (speakers) parts.push(`## Speakers\n\n${speakers}`);
    return parts.join('\n\n');
  };
  
  // Check if at_glance fields have changed
  const hasAtGlanceChanges = () => {
    return (
      data.at_glance_summary !== savedStates.at_glance_summary ||
      data.at_glance_keypoints !== savedStates.at_glance_keypoints ||
      data.at_glance_speakers !== savedStates.at_glance_speakers
    );
  };
  
  // Speaker management functions
  const handleAddSpeaker = () => {
    setData(prev => ({
      ...prev,
      speakers_data: [...(prev.speakers_data || []), { name: '', arguments: [] }]
    }));
    setHasUnsavedChanges(prev => ({ ...prev, speakers: true }));
  };
  
  const handleRemoveSpeaker = (speakerIndex) => {
    setData(prev => {
      const newSpeakers = [...(prev.speakers_data || [])];
      newSpeakers.splice(speakerIndex, 1);
      return { ...prev, speakers_data: newSpeakers };
    });
    setHasUnsavedChanges(prev => ({ ...prev, speakers: true }));
  };
  
  const handleUpdateSpeaker = (speakerIndex, field, value) => {
    setData(prev => {
      const newSpeakers = [...(prev.speakers_data || [])];
      if (!newSpeakers[speakerIndex]) {
        newSpeakers[speakerIndex] = { name: '', arguments: [] };
      }
      newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], [field]: value };
      return { ...prev, speakers_data: newSpeakers };
    });
    setHasUnsavedChanges(prev => ({ ...prev, speakers: true }));
  };
  
  const handleAddArgument = (speakerIndex) => {
    setData(prev => {
      const newSpeakers = [...(prev.speakers_data || [])];
      if (!newSpeakers[speakerIndex]) {
        newSpeakers[speakerIndex] = { name: '', arguments: [] };
      }
      if (!newSpeakers[speakerIndex].arguments) {
        newSpeakers[speakerIndex].arguments = [];
      }
      newSpeakers[speakerIndex].arguments = [
        ...newSpeakers[speakerIndex].arguments,
        { name: '', explanation: '', evidence: '', majorDiscussionPoint: '' }
      ];
      return { ...prev, speakers_data: newSpeakers };
    });
    setHasUnsavedChanges(prev => ({ ...prev, speakers: true }));
  };
  
  const handleRemoveArgument = (speakerIndex, argIndex) => {
    setData(prev => {
      const newSpeakers = [...(prev.speakers_data || [])];
      if (newSpeakers[speakerIndex] && newSpeakers[speakerIndex].arguments) {
        const newArgs = [...newSpeakers[speakerIndex].arguments];
        newArgs.splice(argIndex, 1);
        newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], arguments: newArgs };
      }
      return { ...prev, speakers_data: newSpeakers };
    });
    setHasUnsavedChanges(prev => ({ ...prev, speakers: true }));
  };
  
  const handleUpdateArgument = (speakerIndex, argIndex, field, value) => {
    setData(prev => {
      const newSpeakers = [...(prev.speakers_data || [])];
      if (!newSpeakers[speakerIndex]) {
        newSpeakers[speakerIndex] = { name: '', arguments: [] };
      }
      if (!newSpeakers[speakerIndex].arguments) {
        newSpeakers[speakerIndex].arguments = [];
      }
      if (!newSpeakers[speakerIndex].arguments[argIndex]) {
        newSpeakers[speakerIndex].arguments[argIndex] = { name: '', explanation: '', evidence: '', majorDiscussionPoint: '' };
      }
      const newArgs = [...newSpeakers[speakerIndex].arguments];
      newArgs[argIndex] = { ...newArgs[argIndex], [field]: value };
      newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], arguments: newArgs };
      return { ...prev, speakers_data: newSpeakers };
    });
    setHasUnsavedChanges(prev => ({ ...prev, speakers: true }));
  };

  const handleDataChange = (key, value) => {
    setData(prev => ({ ...prev, [key]: value || '' }));
    
    // Check if there are unsaved changes
    if (key.startsWith('at_glance_')) {
      // Special handling for at_glance structured fields
      setHasUnsavedChanges(prev => ({
        ...prev,
        at_glance: hasAtGlanceChanges(),
      }));
    } else {
      const tabId = tabs.find(t => t.key === key)?.id;
      if (tabId) {
        setHasUnsavedChanges(prev => ({
          ...prev,
          [tabId]: (value || '') !== (savedStates[key] || ''),
        }));
      }
    }
  };

  // Validation functions
  const validateAtGlance = () => {
    const errors = [];
    // At least one field should be filled
    if (!data.at_glance_summary && !data.at_glance_keypoints && !data.at_glance_speakers) {
      errors.push('At least one field (Summary, Keypoints, or Speakers) must be filled');
    }
    return errors;
  };
  
  const validateSpeakers = () => {
    const errors = [];
    if (!data.speakers_data || data.speakers_data.length === 0) {
      // Empty is OK - validation passes
      return errors;
    }
    
    for (let i = 0; i < data.speakers_data.length; i++) {
      const speaker = data.speakers_data[i];
      if (!speaker.name || !speaker.name.trim()) {
        errors.push(`Speaker ${i + 1} must have a name`);
      }
      
      if (speaker.arguments && speaker.arguments.length > 0) {
        for (let j = 0; j < speaker.arguments.length; j++) {
          const arg = speaker.arguments[j];
          if (!arg.name || !arg.name.trim()) {
            errors.push(`Speaker "${speaker.name || `#${i + 1}`}" - Argument ${j + 1} must have a name`);
          }
        }
      }
    }
    
    return errors;
  };
  
  const validateFullReport = () => {
    const errors = [];
    // Full report can be empty, but if it has content, it should be valid markdown
    // Basic validation - just check if it's a string
    if (data.full_report_md && typeof data.full_report_md !== 'string') {
      errors.push('Full report must be valid text');
    }
    return errors;
  };
  
  const validateInDepth = () => {
    const errors = [];
    // In-depth analysis can be empty, but if it has content, it should be valid markdown
    if (data.in_depth_md && typeof data.in_depth_md !== 'string') {
      errors.push('In-depth analysis must be valid text');
    }
    return errors;
  };

  const handleSave = async (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    // Validate before saving
    let validationErrors = [];
    if (tabId === 'at_glance') {
      validationErrors = validateAtGlance();
    } else if (tabId === 'speakers') {
      validationErrors = validateSpeakers();
    } else if (tabId === 'full_report') {
      validationErrors = validateFullReport();
    } else if (tabId === 'analysis') {
      validationErrors = validateInDepth();
    }
    
    if (validationErrors.length > 0) {
      // Show validation errors
      setValidationErrors(prev => ({
        ...prev,
        [tabId]: validationErrors.join('; '),
      }));
      return;
    }

    let payload = {};
    
    // Special handling for at_glance - combine fields into markdown
    if (tabId === 'at_glance') {
      payload.session_at_glance_md = combineAtGlanceToMarkdown(
        data.at_glance_summary,
        data.at_glance_keypoints,
        data.at_glance_speakers
      );
    } else if (tabId === 'speakers') {
      // Special handling for speakers - combine structured data into markdown
      payload.speakers_md = combineSpeakersToMarkdown(data.speakers_data);
    } else {
      payload[tab.key] = data[tab.key];
    }

    try {
      await onSave(payload);
      // Update saved state
      if (tabId === 'at_glance') {
        setSavedStates(prev => ({
          ...prev,
          at_glance_summary: data.at_glance_summary,
          at_glance_keypoints: data.at_glance_keypoints,
          at_glance_speakers: data.at_glance_speakers,
        }));
      } else if (tabId === 'speakers') {
        setSavedStates(prev => ({
          ...prev,
          speakers_data: JSON.parse(JSON.stringify(data.speakers_data)), // Deep copy
        }));
      } else {
        setSavedStates(prev => ({
          ...prev,
          [tab.key]: data[tab.key],
        }));
      }
      setHasUnsavedChanges(prev => ({
        ...prev,
        [tabId]: false,
      }));
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleSaveAll = async () => {
    // Validate all tabs before saving
    const allErrors = {};
    const atGlanceErrors = validateAtGlance();
    const speakersErrors = validateSpeakers();
    const fullReportErrors = validateFullReport();
    const inDepthErrors = validateInDepth();
    
    if (atGlanceErrors.length > 0) allErrors.at_glance = atGlanceErrors.join('; ');
    if (speakersErrors.length > 0) allErrors.speakers = speakersErrors.join('; ');
    if (fullReportErrors.length > 0) allErrors.full_report = fullReportErrors.join('; ');
    if (inDepthErrors.length > 0) allErrors.analysis = inDepthErrors.join('; ');
    
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return;
    }
    
    // Collect ALL fields to ensure complete update on backend
    const payload = {};
    const tabsToSave = [];

    tabs.forEach(tab => {
      if (tab.id === 'at_glance') {
        // Special handling for at_glance - combine fields into markdown
        payload.session_at_glance_md = combineAtGlanceToMarkdown(
          data.at_glance_summary,
          data.at_glance_keypoints,
          data.at_glance_speakers
        );
        if (hasUnsavedChanges[tab.id]) {
          tabsToSave.push(tab.id);
        }
      } else if (tab.id === 'speakers') {
        // Special handling for speakers - combine structured data into markdown
        payload.speakers_md = combineSpeakersToMarkdown(data.speakers_data);
        if (hasUnsavedChanges[tab.id]) {
          tabsToSave.push(tab.id);
        }
      } else {
        // Send all fields, not just unsaved ones, to ensure complete update
        payload[tab.key] = data[tab.key] || '';
        if (hasUnsavedChanges[tab.id]) {
          tabsToSave.push(tab.id);
        }
      }
    });

    // Check if there are any actual changes
    const hasAnyChanges = tabs.some(tab => hasUnsavedChanges[tab.id]);
    if (!hasAnyChanges) {
      return; // No changes to save
    }

    try {
      await onSave(payload);
      // Update all saved states
      setSavedStates(prev => {
        const newStates = { ...prev };
        newStates.at_glance_summary = data.at_glance_summary;
        newStates.at_glance_keypoints = data.at_glance_keypoints;
        newStates.at_glance_speakers = data.at_glance_speakers;
        newStates.speakers_data = JSON.parse(JSON.stringify(data.speakers_data)); // Deep copy
        tabs.forEach(tab => {
          if (tab.key) {
            newStates[tab.key] = data[tab.key] || '';
          }
        });
        return newStates;
      });
      // Clear all unsaved changes
      setHasUnsavedChanges({
        at_glance: false,
        full_report: false,
        speakers: false,
        analysis: false,
      });
    } catch (error) {
      console.error('Save all failed:', error);
    }
  };

  const hasAnyUnsavedChanges = Object.values(hasUnsavedChanges).some(hasChanges => hasChanges);

  const handleCancel = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    // Revert to saved state
    if (tabId === 'at_glance') {
      setData(prev => ({
        ...prev,
        at_glance_summary: savedStates.at_glance_summary,
        at_glance_keypoints: savedStates.at_glance_keypoints,
        at_glance_speakers: savedStates.at_glance_speakers,
      }));
    } else if (tabId === 'speakers') {
      setData(prev => ({
        ...prev,
        speakers_data: JSON.parse(JSON.stringify(savedStates.speakers_data)), // Deep copy
      }));
    } else {
      setData(prev => ({
        ...prev,
        [tab.key]: savedStates[tab.key],
      }));
    }
    setHasUnsavedChanges(prev => ({
      ...prev,
      [tabId]: false,
    }));
  };

  const getTabStatus = (tabId) => {
    if (isSaving) return 'saving';
    if (mergedErrors[tabId]) return 'error';
    if (hasUnsavedChanges[tabId]) return 'unsaved';
    return 'saved';
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onCancel}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Session Details
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{sessionTitle || 'Report Editor'}</h1>
              <p className="text-sm text-gray-500 mt-1">Session ID: {sessionId}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const status = getTabStatus(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    px-4 py-3 text-sm font-medium border-b-2 transition-colors
                    ${activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <span>{tab.label}</span>
                    {status === 'unsaved' && (
                      <span className="h-2 w-2 rounded-full bg-yellow-500" title="Unsaved changes" />
                    )}
                    {status === 'saved' && (
                      <CheckCircleIcon className="h-4 w-4 text-green-500" title="Saved" />
                    )}
                    {status === 'error' && (
                      <XCircleIcon className="h-4 w-4 text-red-500" title="Save error" />
                    )}
                    {status === 'saving' && (
                      <div className="h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" title="Saving..." />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        {/* Save All Button */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {hasAnyUnsavedChanges ? (
              <span>You have unsaved changes across multiple tabs</span>
            ) : (
              <span className="text-green-600">All changes saved</span>
            )}
          </div>
          <button
            onClick={handleSaveAll}
            disabled={isSaving || !hasAnyUnsavedChanges}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg transition-colors
              ${hasAnyUnsavedChanges && !isSaving
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {isSaving ? 'Saving All...' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? 'block' : 'hidden'}
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{tab.label}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {getTabStatus(tab.id) === 'unsaved' && 'You have unsaved changes'}
                    {getTabStatus(tab.id) === 'saved' && 'All changes saved'}
                    {getTabStatus(tab.id) === 'error' && `Error: ${mergedErrors[tab.id] || 'Failed to save'}`}
                    {getTabStatus(tab.id) === 'saving' && 'Saving...'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {hasUnsavedChanges[tab.id] && (
                    <button
                      onClick={() => handleCancel(tab.id)}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => handleSave(tab.id)}
                    disabled={isSaving || !hasUnsavedChanges[tab.id]}
                    className={`
                      px-4 py-2 text-sm font-medium rounded-lg transition-colors
                      ${hasUnsavedChanges[tab.id] && !isSaving
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }
                    `}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              {mergedErrors[tab.id] && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{mergedErrors[tab.id]}</p>
                </div>
              )}

              <div data-color-mode="light" className="mt-4">
                {tab.isStructured && tab.id === 'at_glance' ? (
                  // Structured form for Session at a glance
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Summary
                      </label>
                      <textarea
                        value={data.at_glance_summary || ''}
                        onChange={(e) => handleDataChange('at_glance_summary', e.target.value)}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                        placeholder="Enter session summary..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Keypoints
                      </label>
                      <textarea
                        value={data.at_glance_keypoints || ''}
                        onChange={(e) => handleDataChange('at_glance_keypoints', e.target.value)}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                        placeholder="Enter key points..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Speakers
                      </label>
                      <textarea
                        value={data.at_glance_speakers || ''}
                        onChange={(e) => handleDataChange('at_glance_speakers', e.target.value)}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                        placeholder="Enter speaker information..."
                      />
                    </div>
                  </div>
                ) : tab.isStructured && tab.id === 'speakers' ? (
                  // Structured form for Speakers
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Add speakers and their arguments. Each argument can have explanation, evidence, and discussion points.
                      </p>
                      <button
                        onClick={handleAddSpeaker}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add Speaker
                      </button>
                    </div>
                    
                    {(!data.speakers_data || data.speakers_data.length === 0) ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>No speakers added yet. Click "Add Speaker" to get started.</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {data.speakers_data.map((speaker, speakerIndex) => {
                          const isSpeakerCollapsed = collapsedSpeakers[speakerIndex] === true;
                          return (
                          <div key={speakerIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2 flex-1">
                                <button
                                  type="button"
                                  onClick={() => setCollapsedSpeakers(prev => ({ ...prev, [speakerIndex]: !isSpeakerCollapsed }))}
                                  className="p-1 rounded hover:bg-gray-200 transition-colors"
                                  title={isSpeakerCollapsed ? 'Expand speaker' : 'Collapse speaker'}
                                >
                                  {isSpeakerCollapsed ? (
                                    <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                                  ) : (
                                    <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                                  )}
                                </button>
                                <input
                                  type="text"
                                  value={speaker.name || ''}
                                  onChange={(e) => handleUpdateSpeaker(speakerIndex, 'name', e.target.value)}
                                  placeholder="Speaker name"
                                  className="flex-1 px-3 py-2 text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                                />
                              </div>
                              <button
                                onClick={() => handleRemoveSpeaker(speakerIndex)}
                                className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove speaker"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                            
                            {!isSpeakerCollapsed && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-700">Arguments</h4>
                                <button
                                  onClick={() => handleAddArgument(speakerIndex)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                                >
                                  <PlusIcon className="h-3 w-3" />
                                  Add Argument
                                </button>
                              </div>
                              
                              {(!speaker.arguments || speaker.arguments.length === 0) ? (
                                <p className="text-sm text-gray-500 italic">No arguments added for this speaker.</p>
                              ) : (
                                <div className="space-y-3">
                                  {speaker.arguments.map((argument, argIndex) => {
                                    const argKey = `${speakerIndex}-${argIndex}`;
                                    const isArgCollapsed = collapsedArguments[argKey] === true;
                                    return (
                                    <div key={argIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2 flex-1">
                                          <button
                                            type="button"
                                            onClick={() => setCollapsedArguments(prev => ({ ...prev, [argKey]: !isArgCollapsed }))}
                                            className="p-1 rounded hover:bg-gray-100 transition-colors"
                                            title={isArgCollapsed ? 'Expand argument' : 'Collapse argument'}
                                          >
                                            {isArgCollapsed ? (
                                              <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                                            ) : (
                                              <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                                            )}
                                          </button>
                                          <input
                                            type="text"
                                            value={argument.name || ''}
                                            onChange={(e) => handleUpdateArgument(speakerIndex, argIndex, 'name', e.target.value)}
                                            placeholder="Argument name"
                                            className="flex-1 px-3 py-2 font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                          />
                                        </div>
                                        <button
                                          onClick={() => handleRemoveArgument(speakerIndex, argIndex)}
                                          className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                          title="Remove argument"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                      
                                      {!isArgCollapsed && (
                                      <div className="space-y-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Explanation</label>
                                          <textarea
                                            value={argument.explanation || ''}
                                            onChange={(e) => handleUpdateArgument(speakerIndex, argIndex, 'explanation', e.target.value)}
                                            rows={3}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                                            placeholder="Explain the argument..."
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Evidence</label>
                                          <textarea
                                            value={argument.evidence || ''}
                                            onChange={(e) => handleUpdateArgument(speakerIndex, argIndex, 'evidence', e.target.value)}
                                            rows={2}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                                            placeholder="Supporting evidence..."
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Major Discussion Point</label>
                                          <input
                                            type="text"
                                            value={argument.majorDiscussionPoint || ''}
                                            onChange={(e) => handleUpdateArgument(speakerIndex, argIndex, 'majorDiscussionPoint', e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="Major discussion point..."
                                          />
                                        </div>
                                      </div>
                                      )}
                                    </div>
                                  );})}
                                </div>
                              )}
                            </div>
                            )}
                          </div>
                        );})}
                      </div>
                    )}
                  </div>
                ) : (
                  // Markdown editor for other tabs
                  <>
                    {/* Preview Mode Toggle */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">View Mode:</span>
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setPreviewMode('edit')}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                              previewMode === 'edit'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setPreviewMode('live')}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                              previewMode === 'live'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Live Preview
                          </button>
                          <button
                            onClick={() => setPreviewMode('preview')}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                              previewMode === 'preview'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Preview
                          </button>
                        </div>
                      </div>
                    </div>

                    <MDEditor
                      key={`${tab.id}-${previewMode}`}
                      value={data[tab.key] || ''}
                      onChange={(value) => handleDataChange(tab.key, value)}
                      preview={previewMode}
                      hideToolbar={false}
                      visibleDragBar={true}
                      height={600}
                      data-color-mode="light"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportEditor;

